import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ButtonEditor } from '../../../shared/buttonEditor';
import { ICON_TYPE_BUILTIN } from '../../../shared/buttonEditor/constants';
import {
  isFirmwareSupportedIconKey,
  TOUCH_PANEL_PAYLOAD_LIMITS,
} from '../../../services/touchPanelDeployable/payloadLimits';
import { flowsApi } from '../../../services/flowsApiService';
import {
  PANEL_ACTION_OPEN_SCREEN,
  PANEL_ACTION_HOME,
  PANEL_ACTION_BACK,
  PANEL_ACTION_NONE,
  PANEL_ACTION_FLOW,
  canSavePanelButton,
  buildPanelButtonSavePayload,
  getSortedScreens,
} from './panelModel';

function renderPanelButtonWarnings({ values }) {
  const warnings = [];
  const labelLen = (values.label || '').length;
  const maxLabel = TOUCH_PANEL_PAYLOAD_LIMITS.MAX_BUTTON_LABEL_CHARS;

  if (
    values.iconType === ICON_TYPE_BUILTIN &&
    values.iconKey &&
    !isFirmwareSupportedIconKey(values.iconKey)
  ) {
    warnings.push(
      `Icon “${values.iconKey}” is not in the curated panel icon library — device will show a symbol fallback.`
    );
  }
  if (labelLen > maxLabel) {
    warnings.push(
      `Label is ${labelLen} characters; firmware truncates around ${maxLabel} characters on device.`
    );
  }
  if (warnings.length === 0) return null;

  return (
    <Stack spacing={1}>
      {warnings.map((message) => (
        <Alert key={message} severity="warning" variant="outlined">
          {message}
        </Alert>
      ))}
    </Stack>
  );
}

/**
 * Touch Panel adapter around shared ButtonEditor.
 * Loads tenant flows for Run Flow (same pattern as Home / Incidents button editors).
 */
export default function TouchPanelButtonModal({
  open,
  button,
  panel,
  tenantId,
  onClose,
  onSave,
  onDelete,
}) {
  const screens = useMemo(() => getSortedScreens(panel), [panel]);
  const [flowOptions, setFlowOptions] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const loadFlows = async () => {
      if (!open || !tenantId) return;
      setFlowsLoading(true);
      try {
        const resp = await flowsApi.getAll(tenantId);
        const flows = Array.isArray(resp) ? resp : resp?.data || [];
        const opts = (Array.isArray(flows) ? flows : [])
          .map((f) => {
            const id = f?.flowId || f?.id;
            return {
              id,
              name: f?.flowName || f?.name || id,
            };
          })
          .filter((o) => !!o?.id)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        if (active) setFlowOptions(opts);
      } catch (_) {
        if (active) setFlowOptions([]);
      } finally {
        if (active) setFlowsLoading(false);
      }
    };
    loadFlows();
    return () => {
      active = false;
    };
  }, [open, tenantId]);

  const actions = useMemo(
    () => [
      {
        value: PANEL_ACTION_OPEN_SCREEN,
        label: 'Open Screen',
        renderConfig: ({ values, setValues }) => (
          <FormControl fullWidth>
            <InputLabel id="target-screen-label">Target Screen</InputLabel>
            <Select
              labelId="target-screen-label"
              label="Target Screen"
              value={values.targetScreenId || ''}
              onChange={(e) => setValues({ targetScreenId: e.target.value })}
            >
              <MenuItem value="">Select screen</MenuItem>
              {screens.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      { value: PANEL_ACTION_HOME, label: 'Home' },
      { value: PANEL_ACTION_BACK, label: 'Back' },
      {
        value: PANEL_ACTION_FLOW,
        label: 'Run Flow',
        renderConfig: ({ values, setValues }) => (
          <FormControl fullWidth disabled={flowsLoading}>
            <InputLabel id="panel-flow-select-label">Assigned Flow</InputLabel>
            <Select
              labelId="panel-flow-select-label"
              label="Assigned Flow"
              value={values.flowId || ''}
              onChange={(e) => setValues({ flowId: e.target.value })}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 360,
                    overflowY: 'auto',
                  },
                },
                // Keep menu attached so long lists scroll inside the paper
                disableScrollLock: true,
              }}
              renderValue={(val) => {
                if (!val) return 'None';
                const found = flowOptions.find((o) => o.id === val);
                return found ? found.name : val;
              }}
            >
              <MenuItem value="">None</MenuItem>
              {flowOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
            {flowsLoading ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading flows…</Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {flowOptions.length} flow{flowOptions.length === 1 ? '' : 's'} available. Execution on-device
                follows deploy.
              </Typography>
            )}
          </FormControl>
        ),
      },
      { value: PANEL_ACTION_NONE, label: 'No Action' },
    ],
    [screens, flowOptions, flowsLoading]
  );

  const buttonForEditor = useMemo(() => {
    if (!button) return button;
    return {
      ...button,
      targetScreenId: button.targetScreenId || '',
      actionType: button.actionType || PANEL_ACTION_NONE,
    };
  }, [button]);

  return (
    <ButtonEditor
      open={open}
      button={buttonForEditor}
      title="Edit Panel Button"
      actions={actions}
      canDelete={!!button}
      canSave={canSavePanelButton}
      buildSavePayload={(values) =>
        buildPanelButtonSavePayload(values, { fallbackLabel: button?.label || '' })
      }
      renderExtraContent={renderPanelButtonWarnings}
      onClose={onClose}
      onSave={onSave}
      onDelete={onDelete}
    />
  );
}
